import { type FormEvent, useState } from "react";
import { Scissors, X } from "lucide-react";
import { api } from "../lib/api";

interface Props {
  open: boolean;
  onClose: () => void;
  onClipped: (noteId: string) => void;
}

export function ClipDialog({ open, onClose, onClipped }: Props) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const note = await api.clipUrl(url.trim(), { title: title.trim() || undefined });
      setUrl("");
      setTitle("");
      onClipped(note.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-32"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md space-y-3 rounded-xl border border-zinc-800 bg-zinc-950 p-4 shadow-2xl"
      >
        <header className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-medium">
            <Scissors className="h-4 w-4 text-emerald-400" />
            Web-Clip
          </h2>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-200">
            <X className="h-4 w-4" />
          </button>
        </header>

        <label className="block">
          <span className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
            URL
          </span>
          <input
            type="url"
            required
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
            Titel (optional)
          </span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="(automatisch aus Seite extrahiert)"
            className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
        </label>

        {error && (
          <div className="rounded-md border border-red-900/50 bg-red-950/40 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !url.trim()}
          className="w-full rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
        >
          {busy ? "Clippe…" : "Clippen"}
        </button>
      </form>
    </div>
  );
}
